import { useCallback, useMemo } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';

import { DROPPABLE_FOLDER_PREFIX } from '@/components/organism/SixDetail/dnd/dndTypes';
import type { DragItemData } from '@/components/organism/SixDetail/dnd/dndTypes';
import type { FileEntry } from '@/types/SixDetail.types';

interface UseSixDetailFileBrowserRowDndOptions {
  item: FileEntry;
  isSelected: boolean;
  selectedIds: string[];
  isDragDisabled: boolean;
  dragIdPrefix: string;
  droppableIdPrefix: string;
}

export function useSixDetailFileBrowserRowDnd({
  item,
  isSelected,
  selectedIds,
  isDragDisabled,
  dragIdPrefix,
  droppableIdPrefix,
}: UseSixDetailFileBrowserRowDndOptions) {
  const isNavigationEntry = item.isNavigationEntry === true;
  const isFolder = item.kind === 'folder';
  const isRowDragDisabled = isDragDisabled || isNavigationEntry;

  const dragData: DragItemData = useMemo(
    () => ({
      itemId: item.id,
      selectedIds: isSelected ? selectedIds : [item.id],
    }),
    [item.id, isSelected, selectedIds],
  );

  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: `${dragIdPrefix}${item.id}`,
    data: dragData,
    disabled: isRowDragDisabled,
  });

  const folderDroppablePath = isNavigationEntry
    ? (item.path ?? '')
    : (item.path ?? item.id);

  const droppableId = isFolder
    ? `${droppableIdPrefix}${DROPPABLE_FOLDER_PREFIX}${folderDroppablePath}`
    : `${droppableIdPrefix}nodrop:${item.id}`;

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: droppableId,
    disabled: !isFolder,
  });

  const setRowRef = useCallback(
    (node: HTMLElement | null) => {
      setDragRef(node);
      if (isFolder) setDropRef(node);
    },
    [isFolder, setDragRef, setDropRef],
  );

  return {
    attributes,
    listeners,
    setRowRef,
    isDragging,
    isOver,
    isFolder,
    isRowDragDisabled,
  };
}
